/**
 * StylistProfileNew — revamped profile editor using the shared UI kit.
 * Same feature surface as the original StylistProfileSettings but cleaner UX:
 *   - SectionCard grouping per topic
 *   - PageHeader / PageContainer shells
 *   - LoadingState + EmptyState primitives
 *   - i18n fallback for every visible string
 *   - Inline error messages, ARIA labels, mobile-first layout
 */
import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Box, Typography, Stack, Chip, CircularProgress, MenuItem, TextField, Button, FormControl, FormHelperText, FormControlLabel, Checkbox, Grid, Autocomplete } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CollectionsIcon from '@mui/icons-material/Collections';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { updateStylistDetails, getStylistDetails } from '@services/stylistService';
import { getCertifications } from '@services/certificationService';
import { getSubServiceTypes } from '@services/subServiceTypeService';
import { getShowcases, deleteShowcase } from '@services/showcaseService';
import { translateTextStream } from '@services/aiTranslateService';
import { PageContainer, PageHeader, SectionCard, LoadingState, EmptyState } from '../ui';
import { brand } from '../../theme/tokens';
import ShowcaseCard from '../showcase/ShowcaseCard';
import ShowcaseForm, { type ShowcaseFormProps } from '../showcase/ShowcaseForm';

interface CertificationOption {
  _id: string; name: string; zh_name: string; org_name?: string; zh_org_name?: string;
  scope?: string; zh_scope?: string; recognization?: string; zh_recognization?: string;
  [k: string]: unknown;
}
interface SubServiceTypeOption { _id: string; name: string; zh_name: string; [k: string]: unknown; }
interface ShowcaseMedia { key: string; mediaType: string; }
interface ShowcaseData {
  _id: string; media?: ShowcaseMedia[]; content?: string; status?: string;
  planIds?: string[]; isFromInstagram?: boolean; serviceTypeIds?: string[];
}
interface ProfileFormData {
  _id: string; displayName: string; yearOfExperience: number;
  featuredSkills: Array<{ name: string; zh_name: string }>;
  certifications: string[]; subServiceTypes: string[];
  availability: { dayoff: number[]; startHour: string; endHour: string; leaveDates: string[] };
  description: string; zh_description: string;
  canCommunicateInEnglish: boolean; isAcceptPlatformCoupons: boolean;
  depositOptions: { isRequired: boolean; rules: string; zh_rules: string };
  avatarKey: string | null;
  paymeQRCodeFile: File | null; avatarFile: File | null;
}
interface StylistData {
  _id?: string; displayName?: string; yearOfExperience?: number;
  featuredSkills?: Array<{ name: string; zh_name: string }>;
  certifications?: string[] | CertificationOption[];
  subServiceTypes?: string[] | SubServiceTypeOption[];
  availability?: { dayoff?: number[]; startHour?: string; endHour?: string; leaveDates?: string[] };
  startHour?: string; endHour?: string; leaveDates?: string[];
  description?: string; zh_description?: string;
  canCommunicateInEnglish?: boolean; isAcceptPlatformCoupons?: boolean;
  depositOptions?: { isRequired?: boolean; rules?: string; zh_rules?: string };
  avatarKey?: string | null;
  [k: string]: unknown;
}
interface StylistProfileSettingsProps {
  stylist?: StylistData;
  fetchStylists?: () => void;
  onClose?: () => void;
}
const DAY_OFF_LABELS: Record<number, { en: string; zh: string }> = {
  1: { en: 'Mon', zh: '一' },
  2: { en: 'Tue', zh: '二' },
  3: { en: 'Wed', zh: '三' },
  4: { en: 'Thu', zh: '四' },
  5: { en: 'Fri', zh: '五' },
  6: { en: 'Sat', zh: '六' },
  7: { en: 'Sun', zh: '日' },
};

const StylistProfileNew = ({ stylist, fetchStylists, onClose }: StylistProfileSettingsProps): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showcases, setShowcases] = useState<ShowcaseData[]>([]);
  const [showcaseModalOpen, setShowcaseModalOpen] = useState<boolean>(false);
  const [editingShowcase, setEditingShowcase] = useState<ShowcaseData | null>(null);

  const { register, handleSubmit, setValue, control, reset, formState: { errors }, watch } = useForm<ProfileFormData>({
    defaultValues: {
      _id: stylist?._id || '',
      displayName: stylist?.displayName || '',
      yearOfExperience: stylist?.yearOfExperience || 0,
      featuredSkills: stylist?.featuredSkills || [],
      certifications: Array.isArray(stylist?.certifications) ? (typeof stylist!.certifications[0] === 'string' ? stylist!.certifications as string[] : (stylist!.certifications as CertificationOption[]).map((c) => c._id)) : [],
      subServiceTypes: Array.isArray(stylist?.subServiceTypes) ? (typeof stylist!.subServiceTypes[0] === 'string' ? stylist!.subServiceTypes as string[] : (stylist!.subServiceTypes as SubServiceTypeOption[]).map((s) => s._id)) : [],
      availability: {
        dayoff: stylist?.availability?.dayoff || [],
        startHour: stylist?.availability?.startHour || '10:00',
        endHour: stylist?.availability?.endHour || '20:00',
        leaveDates: stylist?.availability?.leaveDates || [],
      },
      description: stylist?.description || '',
      zh_description: stylist?.zh_description || '',
      canCommunicateInEnglish: stylist?.canCommunicateInEnglish ?? false,
      isAcceptPlatformCoupons: stylist?.isAcceptPlatformCoupons ?? false,
      depositOptions: {
        isRequired: stylist?.depositOptions?.isRequired ?? false,
        rules: stylist?.depositOptions?.rules || '',
        zh_rules: stylist?.depositOptions?.zh_rules || '',
      },
      avatarKey: stylist?.avatarKey || null,
      paymeQRCodeFile: null,
      avatarFile: null,
    },
  });
  const { fields: skillFields, append: appendSkill, remove: removeSkill } = useFieldArray({ control, name: 'featuredSkills' });

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      try {
        setIsLoading(true);
        const sid = stylist?._id;
        if (!sid) { setIsLoading(false); return; }
        const [profileData, showcasesData, _certs, _subs] = await Promise.all([
          getStylistDetails(sid),
          getShowcases(sid),
          getCertifications().catch(() => []),
          getSubServiceTypes().catch(() => []),
        ]);
        if (!mounted) return;
        if (profileData) {
          reset({
            ...profileData,
            _id: profileData._id || sid,
            certifications: Array.isArray(profileData.certifications) ? profileData.certifications : [],
            subServiceTypes: Array.isArray(profileData.subServiceTypes) ? profileData.subServiceTypes : [],
            availability: profileData.availability || { dayoff: [], startHour: '10:00', endHour: '20:00', leaveDates: [] },
          });
        }
        if (Array.isArray(showcasesData)) setShowcases(showcasesData);
        else if (showcasesData && Array.isArray((showcasesData as { data?: ShowcaseData[] }).data)) setShowcases((showcasesData as { data: ShowcaseData[] }).data as ShowcaseData[]);
      } catch (err) {
        console.error(err);
        toast.error(t('stylistProfileSettings.loadError', 'Failed to load profile.'));
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    loadAll();
    return () => { mounted = false; };
  }, [stylist?._id, reset, t]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsSaving(true);
      await updateStylistDetails(data as unknown as Parameters<typeof updateStylistDetails>[0]);
      toast.success(t('stylistProfileSettings.saveSuccess', 'Profile updated.'));
      if (fetchStylists) fetchStylists();
    } catch {
      toast.error(t('stylistProfileSettings.saveError', 'Failed to save.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteShowcase = async (id: string) => {
    if (!window.confirm(t('common.confirmDelete', 'Are you sure?'))) return;
    try {
      await deleteShowcase(id);
      setShowcases((s) => s.filter((x) => x._id !== id));
      toast.success(t('stylistProfileSettings.showcaseDeleted', 'Showcase deleted.'));
    } catch {
      toast.error(t('common.errorOccurred'));
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState count={4} variant="cards" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('stylistProfileSettings.title', 'Stylist profile')}
        subtitle={t('stylistProfileNew.subtitle', 'Manage your public profile, services and availability.')}
        icon={<PersonIcon />}
        classicPath="/stylist-profile"
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5}>
          <SectionCard title={t('stylistProfileSettings.basicInfo', 'Basic info')} icon={<PersonIcon />}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('stylistProfileSettings.displayName', 'Display name')}
                  fullWidth required
                  {...register('displayName', { required: true })}
                  error={!!errors.displayName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('stylistProfileSettings.yearOfExperience', 'Years of experience')}
                  type="number" fullWidth
                  {...register('yearOfExperience', { valueAsNumber: true, min: 0 })}
                  error={!!errors.yearOfExperience}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('stylistProfileSettings.description', 'Description (English)')}
                  multiline rows={3} fullWidth
                  {...register('description')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('stylistProfileSettings.zh_description', '描述（中文）')}
                  multiline rows={3} fullWidth
                  {...register('zh_description')}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title={t('stylistProfileSettings.featuredSkills', 'Featured skills')} icon={<StarIcon />}>
            {skillFields.map((f, idx) => (
              <Grid container spacing={1} key={f.id} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label={t('stylistProfileSettings.skillEn', 'Skill (English)')}
                    fullWidth size="small"
                    {...register(`featuredSkills.${idx}.name` as const)}
                  />
                </Grid>
                <Grid size={{ xs: 10, sm: 5 }}>
                  <TextField
                    label={t('stylistProfileSettings.skillZh', '技能（中文）')}
                    fullWidth size="small"
                    {...register(`featuredSkills.${idx}.zh_name` as const)}
                  />
                </Grid>
                <Grid size={{ xs: 2, sm: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button size="small" color="error" onClick={() => removeSkill(idx)}>×</Button>
                </Grid>
              </Grid>
            ))}
            <Button size="small" onClick={() => appendSkill({ name: '', zh_name: '' })}>
              + {t('stylistProfileSettings.addSkill', 'Add skill')}
            </Button>
          </SectionCard>

          <SectionCard title={t('stylistProfileSettings.availability', 'Availability')} icon={<EventAvailableIcon />}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label={t('stylistProfileSettings.startHour', 'Start hour')}
                  fullWidth {...register('availability.startHour')} placeholder="10:00"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label={t('stylistProfileSettings.endHour', 'End hour')}
                  fullWidth {...register('availability.endHour')} placeholder="20:00"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('stylistProfileSettings.dayoff', 'Days off')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <Chip
                      key={d}
                      label={isZh ? DAY_OFF_LABELS[d].zh : DAY_OFF_LABELS[d].en}
                      size="small"
                      clickable
                      color={watch('availability.dayoff')?.includes(d) ? 'primary' : 'default'}
                      onClick={() => {
                        const current: number[] = watch('availability.dayoff') || [];
                            setValue('availability.dayoff', current.includes(d) ? current.filter((x) => x !== d) : [...current, d]);
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title={t('stylistProfileSettings.depositOptions', 'Deposit options')} icon={<PaymentsIcon />}>
            <FormControlLabel
              control={<Checkbox defaultChecked={!!watch('depositOptions.isRequired')} {...register('depositOptions.isRequired')} />}
              label={t('stylistProfileSettings.depositRequired', 'Require deposit to confirm booking')}
            />
            <TextField sx={{ mt: 1.5 }} label={t('stylistProfileSettings.depositRulesEn', 'Deposit rules (English)')}
              multiline rows={2} fullWidth {...register('depositOptions.rules')}
            />
            <TextField sx={{ mt: 1.5 }} label={t('stylistProfileSettings.depositRulesZh', '按金規則（中文）')}
              multiline rows={2} fullWidth {...register('depositOptions.zh_rules')}
            />
          </SectionCard>

          <SectionCard
            title={t('stylistProfileSettings.showcases', 'Showcases')}
            icon={<CollectionsIcon />}
            action={
              <Button size="small" variant="outlined" onClick={() => { setEditingShowcase(null); setShowcaseModalOpen(true); }}>
                + {t('stylistProfileSettings.addShowcase', 'Add')}
              </Button>
            }
          >
            {showcases.length === 0 ? (
              <EmptyState
                title={t('stylistProfileSettings.noShowcasesYet', 'No showcases yet')}
                description={t('stylistProfileSettings.noShowcasesHint', 'Add a portfolio image to start attracting bookings.')}
              />
            ) : (
              <Grid container spacing={2}>
                {showcases.map((s) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={s._id}>
                    <ShowcaseCard
                      showcase={s as unknown as ShowcaseFormProps['showcase']}
                      onEdit={() => { setEditingShowcase(s); setShowcaseModalOpen(true); }}
                      onDelete={() => handleDeleteShowcase(s._id)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {onClose && <Button onClick={onClose} disabled={isSaving}>{t('common.cancel', 'Cancel')}</Button>}
            <Button
              type="submit" variant="contained" size="large" disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minWidth: 160, bgcolor: brand.gold, '&:hover': { bgcolor: brand.goldDark } }}
            >
              {isSaving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          </Box>
        </Stack>
      </form>

      {showcaseModalOpen && (
        <ShowcaseForm
          open={showcaseModalOpen}
          onClose={() => { setShowcaseModalOpen(false); setEditingShowcase(null); }}
          showcase={editingShowcase as unknown as ShowcaseFormProps['showcase']}
          onSaved={() => {
            setShowcaseModalOpen(false);
            setEditingShowcase(null);
            if (stylist?._id) {
              getShowcases(stylist._id).then((d: unknown) => {
                const list = Array.isArray(d) ? d : (d as { data?: ShowcaseData[] })?.data || [];
                setShowcases(list as ShowcaseData[]);
              }).catch(() => {});
            }
          }}
        />
      )}
    </PageContainer>
  );
};

export default StylistProfileNew;
